import { AfterViewChecked, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { cloneDeep } from 'lodash';
import { finalize, iif, of, switchMap, tap } from 'rxjs';
import { LeadInjector } from 'src/app/core/models/lead/lead-injector.model';
import { copiedToClipboard, copiedToClipboardError } from 'src/app/shared/utils/events/copied-to-clipboard.action';
import { ConfirmDialogComponent } from '../../dialogs/confirm-dialog/confirm-dialog.component';
import { GlobalSpinnerActions } from '../../global-spinner/actions-types';
import { CloseSidenavAction } from '../sidenav.actions';
import { LeadInjectorInfoService } from './services/lead-injector-info.service';
import { LobbyErrorHandlerService } from 'src/app/core/services/lobby-error-handler.service';
import { LeadInjectorDashBoardAction } from 'src/app/features/lead-injector/lead-injector.actions';
import { SocialFormsFacebookService } from 'src/app/features/lead-injector/services/social-forms-facebook.service';
import { FacebookFormConfiguration } from 'src/app/core/models/social-forms-configurations/facebook-form-configuration.model';
import { LeadInjectorAction } from 'src/app/core/events/lead-injector.actions';
import { time } from '../../inputs/time-input/time-input.component';

export interface LeadInjectorInfoDetailsForm extends FormGroup {
  controls: {
    description: FormControl<string | null>
    note: FormControl<string | null>

    activationBegin: FormControl<Date | null>
    activationEnd: FormControl<Date | null>

    isEnabled: FormControl<boolean | null>
    isAutoOffer: FormControl<boolean | null>

    curlSnipped: FormControl<string | null>
    htmlSnipped: FormControl<string | null>

    domains: FormArray<FormGroup<{
      domain: FormControl<string | null>
    }>>

    ipAddresses: FormArray<FormGroup<{
      ip: FormControl<string | null>
    }>>

  }
}

@Component({
    selector: 'app-sidenav-content-lead-injector-info-container',
    templateUrl: './sidenav-content-lead-injector-info-container.component.html',
    styleUrls: ['./sidenav-content-lead-injector-info-container.component.scss'],
    standalone: false
})
export class SidenavContentLeadInjectorInfoContainerComponent implements OnInit, AfterViewChecked {

  @Input() set injector(injector: LeadInjector | null) {
    if (injector) {
      this._injector = cloneDeep(injector)
      this.injectorFirstState = cloneDeep(injector)
      this.timeBegin = { hours: this.convertToLocalTimezone(this._injector.activationBegin)?.getHours() ?? 0, minutes: this.convertToLocalTimezone(this._injector.activationBegin)?.getMinutes() ?? 0 }
      this.timeEnd = { hours: this.convertToLocalTimezone(this._injector.activationEnd)?.getHours() ?? 0, minutes: this.convertToLocalTimezone(this._injector.activationEnd)?.getMinutes() ?? 0 }
      this.form = this.createFormGroup(injector!)
      this.subscribeToDetailsForm(this.form)
      this.isCreatingNewInjector = !injector.externalId

      if (injector?.socialChannels?.includes('facebook')) this.retriveFacebookFormConfigurations(injector)

    } else {
      this._injector = undefined
      this.injectorFirstState = undefined
      this.form = undefined

      this.injectorFacebookFormConfigurations = []
    }

    this.cdr.markForCheck()
  }

  get injector() { return this._injector! }

  htmlSnippedEncoding: 'multipart/form-data' | 'application/x-www-form-urlencoded' = 'application/x-www-form-urlencoded'

  form: LeadInjectorInfoDetailsForm | undefined
  injectorFirstState: LeadInjector | undefined
  timeBegin!: time;
  timeEnd!: time;
  isCreatingNewInjector: boolean = false

  injectorFacebookFormConfigurations: FacebookFormConfiguration[] = []

  constructor(private readonly appStore: Store,
    private readonly leadInjectorInfoService: LeadInjectorInfoService,
    private readonly dialog: MatDialog,
    private readonly cdr: ChangeDetectorRef,
    private readonly errorHandlerService: LobbyErrorHandlerService,
    private readonly socialFormsFacebookService: SocialFormsFacebookService) { }

  private _injector: LeadInjector | undefined

  ngOnInit(): void {
  }

  ngAfterViewChecked(): void {
    this.cdr.detectChanges();
  }

  public onClipboardCopy(success: boolean): void {
    success ? this.appStore.dispatch(copiedToClipboard()) : this.appStore.dispatch(copiedToClipboardError())
  }

  get domains() {
    return this.form?.get('domains') as FormArray
  }
  get ipAddresses() {
    return this.form?.get('ipAddresses') as FormArray
  }

  addElementToFormArray(formArray: FormArray, controlName: string, validator?: ValidatorFn) {
    const hasNoEmptyElements = formArray.controls.every(control => {
      const element = control.get(controlName)
      return element && element.value.trim() !== ''
    })
    if (hasNoEmptyElements) {
      const newControl = new FormGroup({
        [controlName]: new FormControl('', validator),
      });
      formArray.push(newControl)
    }
  }

  removeElementFromFormArray(formArray: FormArray, index: number) {
    formArray?.removeAt(index)
    this.form?.markAsDirty()
  }

  onCloseClick() {
    this.close()
  }

  onCancelClick() {
    this.close()
  }

  onSaveClick() {
    of(void 0).pipe(
      tap(() => this.appStore.dispatch(GlobalSpinnerActions.showGlobalSpinner())),
      switchMap(() => iif(() => this.isCreatingNewInjector,
        this.leadInjectorInfoService.createLeadInjector$(this.injector!)
          .pipe(tap((result) => this.appStore.dispatch(LeadInjectorAction.leadInjectorCreated(result)))),
        this.leadInjectorInfoService.updateLeadInjectorInfo$(this.injector!, this.injectorFirstState!)
          .pipe(tap((result) => this.appStore.dispatch(LeadInjectorAction.leadInjectorModified(result))))
      )),
      this.errorHandlerService.handleError,
      finalize(() => this.appStore.dispatch(GlobalSpinnerActions.hideGlobalSpinner())),
      tap(() => this.close(false))
    )
      .subscribe()
  }

  onGoSocialForm(configuration: FacebookFormConfiguration) {
    this.appStore.dispatch(LeadInjectorDashBoardAction.socialFormFacebookInfoView(configuration.id))
  }

  onSetEndDateClick() {
    this.form?.controls.activationEnd.setValue(new Date(new Date().setDate(new Date().getDate() + 1)))
    this.form?.markAsDirty()
  }
  onTimeBeginChange(value: time) {
    this.timeBegin = value
    this.form?.markAsDirty()
    this.updateActivationBegin(this._injector!.activationBegin)
  }
  onTimeEndChange(value: time) {
    this.timeEnd = value
    this.form?.markAsDirty()
    this.updateActivationEnd(this._injector!.activationEnd)
  }
  updateActivationBegin(date: any) {
    date?.setHours(this.timeBegin.hours, this.timeBegin.minutes)
    this._injector!.activationBegin = date
  }
  updateActivationEnd(date: any) {
    date?.setHours(this.timeEnd.hours, this.timeEnd.minutes)
    this._injector!.activationEnd = date
  }
  private close(askConfirmationIfNecessary: boolean = true) {
    if (this.form!.dirty && askConfirmationIfNecessary) {
      const confirmDialog = this.dialog.open(ConfirmDialogComponent, ConfirmDialogComponent.createConfigurations(
        {
          title: 'Conferma',
          message: 'Modifiche non salvate. Se procedi saranno perdute',
          confirmButtonText: 'Procedi',
          cancelButtonText: 'Annulla'
        }
      ))

      confirmDialog.afterClosed().subscribe(result => {
        if (result) this.appStore.dispatch(CloseSidenavAction())
      })
    }
    else this.appStore.dispatch(CloseSidenavAction())
  }

  private createFormGroup(injector: LeadInjector): LeadInjectorInfoDetailsForm {
    return new FormBuilder().group(
      {
        description: new FormControl<string>(injector.description),
        note: new FormControl<string>(injector.note),

        activationBegin: new FormControl<Date | null>(injector.activationBegin),
        activationEnd: new FormControl<Date | null>(injector.activationEnd),

        isEnabled: new FormControl<boolean>(injector.isEnabled),
        isAutoOffer: new FormControl<boolean>(injector.isAutoOfferEnabled),

        curlSnipped: new FormControl<string>(''),
        htmlSnipped: new FormControl<string>(''),
        domains: new FormArray(
          (injector.domains || []).map(domain => {
            return new FormGroup({
              domain: new FormControl<string>(domain, [this.validDomainValidator()])
            })
          })
        ),
        ipAddresses: new FormArray(
          (injector.ipAddresses || []).map(ip => {
            return new FormGroup({
              ip: new FormControl<string>(ip, [this.validIPValidator()])
            })
          })
        )
      }
    )
  }

  private subscribeToDetailsForm(form: LeadInjectorInfoDetailsForm) {

    form.controls.description.valueChanges
      .subscribe(value => this._injector!.description = value!)

    form.controls.note.valueChanges
      .subscribe(value => this._injector!.note = value!)

    form.controls.activationBegin.valueChanges
      .subscribe(value => this.updateActivationBegin(value))

    form.controls.activationEnd.valueChanges
      .subscribe(value => this.updateActivationEnd(value))

    form.controls.isEnabled.valueChanges
      .subscribe(value => this._injector!.isEnabled = value!)

    form.controls.isAutoOffer.valueChanges
      .subscribe(value => this._injector!.isAutoOfferEnabled = value!)

    form.controls.domains.valueChanges
      .subscribe((value: Partial<{ domain: string | null; }>[]) => {
        this._injector!.domains = value
          .map(item => item.domain)
          .filter((domain): domain is string =>
            domain !== null && domain !== undefined && domain !== '')
      })

    form.controls.ipAddresses.valueChanges
      .subscribe((value: Partial<{ ip: string | null; }>[]) => {
        this._injector!.ipAddresses = value
          .map(item => item.ip)
          .filter((ip): ip is string =>
            ip !== null && ip !== undefined && ip !== '')
      })
  }

  validDomainValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value
      if (!value) {
        return null
      }
      const domainRegex: RegExp = /^([a-zA-Z0-9]([a-zA-Z0-9\-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,6}(\n|$)?$/;
      return domainRegex.test(value) ? null : { invalidDomain: true }
    }
  }

  validIPValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (!value) {
        return null
      }
      const ipRegex = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/
      const isValidIP = ipRegex.test(value)
      const isValid = isValidIP
      return isValid ? null : { invalidIP: true }
    }
  }

  private retriveFacebookFormConfigurations(injector: LeadInjector) {
    of(void 0).pipe(
      tap(() => this.appStore.dispatch(GlobalSpinnerActions.showGlobalSpinner())),
      switchMap(() => this.socialFormsFacebookService.searchFacebookForms$(0, 100, null, injector.id).pipe(
        tap(x => console.log('forms', x)),
        this.errorHandlerService.handleError
      )),
      tap(configurations => this.injectorFacebookFormConfigurations = configurations),
      tap(() => this.appStore.dispatch(GlobalSpinnerActions.hideGlobalSpinner()))
    ).subscribe()
  }

  private convertToLocalTimezone(date: Date | null): Date | null {
    if (!date) return null
    const localTimezoneOffset = date.getTimezoneOffset() * 60000
    return new Date(date.getTime() - localTimezoneOffset)
  }

}
